import type { Difficulty, MCQQuestion } from './types';

function mulberry32(seed: number): () => number {
  let state = seed | 0;
  return function () {
    state = (state + 0x6d2b79f5) | 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function randInt(rand: () => number, min: number, max: number): number {
  return Math.floor(rand() * (max - min + 1)) + min;
}

function readNumber(n: number): string {
  if (n === 0) return '零';
  const parts: string[] = [];
  const qian = Math.floor(n / 1000);
  const bai  = Math.floor((n % 1000) / 100);
  const shi  = Math.floor((n % 100) / 10);
  const ge   = n % 10;
  if (qian > 0) parts.push(qian + '千');
  if (bai > 0) parts.push(bai + '百');
  if (shi > 0) parts.push(shi + '十');
  if (ge > 0)  parts.push(String(ge));
  return parts.join('');
}

type QType = 'read' | 'write' | 'placevalue' | 'compare';

/**
 * N-3-1: three-down number recognition 1000-9999
 */
export function generateNumberThousand(seed: number, difficulty: Difficulty): MCQQuestion {
  const rand = mulberry32(seed);

  const qTypes: QType[] = difficulty === 1
    ? ['read', 'write']
    : difficulty === 2
    ? ['read', 'write', 'placevalue']
    : ['placevalue', 'compare', 'compare'];

  const qType = qTypes[randInt(rand, 0, qTypes.length - 1)]!;

  const maxN = difficulty === 1 ? 4999 : 9999;
  const n = randInt(rand, 1000, maxN);

  let prompt = '';
  let correct = '';
  let options: string[] = [];
  let explanation = '';

  if (qType === 'read') {
    const reading = readNumber(n);
    prompt = n + ' 要怎麼讀？';
    correct = reading;
    const adj1 = randInt(rand, 1, 100);
    const adj2 = randInt(rand, 1, Math.min(100, n - 1000));
    const adj3 = randInt(rand, 101, 500);
    const w1 = readNumber(n + adj1 <= 9999 ? n + adj1 : n - adj1);
    const w2 = readNumber(n - adj2 >= 1000 ? n - adj2 : n + adj2);
    const w3 = readNumber(n + adj3 <= 9999 ? n + adj3 : n - adj3);
    options = [correct, w1, w2, w3];
    explanation = n + ' 的千位是 ' + Math.floor(n / 1000) + '，百位是 ' + Math.floor((n % 1000) / 100) + '，十位是 ' + Math.floor((n % 100) / 10) + '，個位是 ' + (n % 10) + '，所以讀作「' + reading + '」。';

  } else if (qType === 'write') {
    const reading = readNumber(n);
    prompt = '「' + reading + '」是哪個數？';
    correct = String(n);
    const d1 = randInt(rand, 1, 100);
    const d2 = randInt(rand, 101, 500);
    const d3 = randInt(rand, 501, 999);
    const w1 = String(n + d1 <= 9999 ? n + d1 : n - d1);
    const w2 = String(n + d2 <= 9999 ? n + d2 : n - d2);
    const w3 = String(n + d3 <= 9999 ? n + d3 : n - d3);
    options = [correct, w1, w2, w3];
    explanation = '「' + reading + '」= ' + n + '。';

  } else if (qType === 'placevalue') {
    const places = ['千位', '百位', '十位', '個位'];
    const placeIdx = randInt(rand, 0, 3);
    const place = places[placeIdx]!;
    const divisors = [1000, 100, 10, 1];
    const mods = [10000, 1000, 100, 10];
    const digit = Math.floor((n % mods[placeIdx]!) / divisors[placeIdx]!);
    prompt = n + ' 的' + place + '是幾？';
    correct = String(digit);
    const wrongs = [0,1,2,3,4,5,6,7,8,9]
      .filter(d => d !== digit)
      .sort(() => rand() - 0.5)
      .slice(0, 3)
      .map(String);
    options = [correct, ...wrongs];
    explanation = n + ' 的' + place + '是 ' + digit + '。';

  } else {
    const a = randInt(rand, 1000, 9999);
    const b = randInt(rand, 1000, 9999);
    const c = randInt(rand, 1000, 9999);
    const largest = Math.max(n, a, b, c);
    correct = String(largest);
    const nums = [n, a, b, c];
    const uniqueSet = new Set(nums.map(String));
    while (uniqueSet.size < 4) uniqueSet.add(String(randInt(rand, 1000, 9999)));
    options = [...uniqueSet].slice(0, 4);
    if (!options.includes(correct)) options[0] = correct;
    explanation = '比較四個數，' + correct + ' 最大。';
  }

  const seen = new Set();
  const uniqueOpts: string[] = [];
  for (const o of options) {
    if (!seen.has(o)) { seen.add(o); uniqueOpts.push(o); }
  }
  while (uniqueOpts.length < 4) {
    const extra = String(randInt(rand, 1000, 9999));
    if (!seen.has(extra) && extra !== correct) { seen.add(extra); uniqueOpts.push(extra); }
  }
  if (!uniqueOpts.includes(correct)) uniqueOpts[0] = correct;

  const shuffled = uniqueOpts.slice(0, 4).sort(() => rand() - 0.5);

  return {
    kind: 'mcq',
    prompt,
    options: shuffled,
    correctAnswer: shuffled.indexOf(correct),
    explanation,
    kp: 'math.number.thousand',
  };
}
